import { ModalBody, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import DateTimePickerField from 'components/forms/dateTimePickerField/DateTimePickerField';
import FormWithProvider from 'components/forms/form/Form';
import FormFieldset from 'components/forms/form/FormFieldset';
import TimezoneSelectField from 'components/forms/timezoneSelectField/TimezoneSelectField';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import useFetchLeadForLeadCell from 'pages/automations/common/cells/lead/useFetchLeadForLeadCell';
import useFetchScheduledMessageByUid from 'components/domain/automation/scheduledMessages/useFetchScheduledMessageByUid';
import { ScheduledMessage } from 'models/automation/ScheduledMessages';
import {
  getFormDefaultValues,
  scheduledMessagesRescheduleModalSchema,
} from './ScheduledMessagesRescheduleModal.schema';
import { ParagraphStyled } from './ScheduledMessagesRescheduleModal.styles';
import ScheduledMessagesRescheduleModalFooter from './ScheduledMessagesRescheduleModalFooter';
import useScheduledMessageRescheduleModalFormSubmit from './useScheduledMessageRescheduleModalFormSubmit';

interface ScheduledMessagesRescheduleModalProps {
  scheduledMessageUid: string;
  closeModal: () => void;
}

const ScheduledMessagesRescheduleModal = ({
  scheduledMessageUid,
  closeModal,
}: ScheduledMessagesRescheduleModalProps) => {
  const { t } = useTranslation();

  const { data: scheduledMessage, isLoading } = useFetchScheduledMessageByUid({
    scheduledMessageUid,
  });

  const { lead, isFetching } = useFetchLeadForLeadCell({
    leadUid: scheduledMessage?.leadUid as string,
    enabled: !!scheduledMessage?.leadUid,
  });

  const { onSubmit } = useScheduledMessageRescheduleModalFormSubmit({
    scheduledMessage: scheduledMessage as ScheduledMessage,
    closeModal,
  });

  if (isLoading || isFetching || !scheduledMessage) {
    return <LoadingPlaceholder />;
  }

  return (
    <FormWithProvider
      defaultValues={getFormDefaultValues({ lead, scheduledMessage })}
      onSubmit={onSubmit}
      resolver={scheduledMessagesRescheduleModalSchema()}
      noValidate
      dirtyChecker
    >
      <FormFieldset>
        <ModalBody>
          <Row className="d-flex flex-column">
            <ParagraphStyled>
              {t(
                'pageAutomations.scheduledMessages.modalReschedule.description',
                {
                  name: scheduledMessage.messageName,
                  recipients:
                    scheduledMessage.triggerRecipients?.join(', ') ?? '-',
                },
              )}
            </ParagraphStyled>
            <TimezoneSelectField
              disabled
              label={t(
                'pageAutomations.scheduledMessages.modalReschedule.timezone',
              )}
              name="timezone"
            />
            <DateTimePickerField
              label={t(
                'pageAutomations.scheduledMessages.modalReschedule.date',
              )}
              name="date"
            />
            <DateTimePickerField
              label={t('common.time')}
              name="time"
              timeOnly
            />
          </Row>
        </ModalBody>
        <ScheduledMessagesRescheduleModalFooter closeModal={closeModal} />
      </FormFieldset>
    </FormWithProvider>
  );
};

export default ScheduledMessagesRescheduleModal;
